import * as THREE from "three";

export interface CameraRigOptions {
  /** Distance from the focus point, in world units. */
  distance?: number;
  minDistance?: number;
  maxDistance?: number;
  /** Starting pitch in radians; positive looks down at the character. */
  pitch?: number;
  minPitch?: number;
  maxPitch?: number;
  /** How high above the actor's feet the camera aims. */
  focusHeight?: number;
  /** Seconds of no camera input before the view drifts back behind the hero. */
  recenterDelay?: number;
}

/**
 * Third-person orbit camera — the modern JRPG default. It sits behind and
 * slightly above the hero, is steered with the mouse, pulls in when a wall
 * gets between it and the character, and drifts back to centre if the player
 * stops touching it while running.
 */
export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;

  /** Direction the camera looks along the ground plane, in radians. */
  yaw = 0;
  pitch: number;
  distance: number;

  private readonly minDistance: number;
  private readonly maxDistance: number;
  private readonly minPitch: number;
  private readonly maxPitch: number;
  private readonly focusHeight: number;
  private readonly recenterDelay: number;

  private currentDistance: number;
  private idleTime = 0;
  private readonly focus = new THREE.Vector3();
  private readonly desired = new THREE.Vector3();
  private readonly raycaster = new THREE.Raycaster();

  constructor(options: CameraRigOptions = {}) {
    this.camera = new THREE.PerspectiveCamera(58, 1, 0.1, 300);
    this.distance = options.distance ?? 6.2;
    this.currentDistance = this.distance;
    this.minDistance = options.minDistance ?? 2.8;
    this.maxDistance = options.maxDistance ?? 13;
    // Looking slightly down over the party's heads, which is where this genre
    // has parked its camera since the series went 3D.
    this.pitch = options.pitch ?? 0.42;
    this.minPitch = options.minPitch ?? -0.1;
    this.maxPitch = options.maxPitch ?? 1.05;
    this.focusHeight = options.focusHeight ?? 1.6;
    this.recenterDelay = options.recenterDelay ?? 1.4;
  }

  /** Unit vector the camera is facing, flattened onto the ground. */
  get forward(): { x: number; z: number } {
    return { x: Math.sin(this.yaw), z: Math.cos(this.yaw) };
  }

  /** Unit vector to the camera's right, flattened onto the ground. */
  get right(): { x: number; z: number } {
    return { x: Math.cos(this.yaw), z: -Math.sin(this.yaw) };
  }

  /** Snap directly behind an actor facing `angle`, with no easing. */
  snapBehind(angle: number, target: THREE.Vector3): void {
    this.yaw = angle;
    this.currentDistance = this.distance;
    this.apply(target, 1);
  }

  /** Mouse/stick look, in radians. */
  look(deltaYaw: number, deltaPitch: number): void {
    if (deltaYaw === 0 && deltaPitch === 0) return;
    this.yaw -= deltaYaw;
    this.pitch = Math.min(this.maxPitch, Math.max(this.minPitch, this.pitch + deltaPitch));
    this.idleTime = 0;
  }

  zoom(delta: number): void {
    if (delta === 0) return;
    this.distance = Math.min(this.maxDistance, Math.max(this.minDistance, this.distance + delta));
  }

  /**
   * @param target        the actor's feet
   * @param travelAngle   direction the hero is running, or null when still
   * @param obstacles     meshes the camera should not sit inside
   */
  update(
    dt: number,
    target: THREE.Vector3,
    travelAngle: number | null,
    obstacles: THREE.Object3D[] = [],
  ): void {
    this.idleTime += dt;

    // Ease the view back behind the hero once the player lets go of the mouse.
    if (travelAngle !== null && this.idleTime > this.recenterDelay) {
      let delta = travelAngle - this.yaw;
      while (delta > Math.PI) delta -= Math.PI * 2;
      while (delta < -Math.PI) delta += Math.PI * 2;
      this.yaw += delta * Math.min(1, dt * 1.6);
    }

    this.apply(target, Math.min(1, dt * 10), obstacles);
  }

  private apply(target: THREE.Vector3, lerp: number, obstacles: THREE.Object3D[] = []): void {
    this.focus.set(target.x, target.y + this.focusHeight, target.z);

    const horizontal = Math.cos(this.pitch);
    const offset = new THREE.Vector3(
      -Math.sin(this.yaw) * horizontal,
      Math.sin(this.pitch),
      -Math.cos(this.yaw) * horizontal,
    );

    let distance = this.distance;

    // Pull in if something solid is between the hero and the camera.
    if (obstacles.length > 0) {
      this.raycaster.set(this.focus, offset);
      this.raycaster.far = distance + 0.4;
      const hits = this.raycaster.intersectObjects(obstacles, false);
      if (hits.length > 0) distance = Math.max(this.minDistance * 0.5, hits[0].distance - 0.35);
    }

    // Snapping closer must be instant or the camera ends up inside the wall;
    // easing back out can be leisurely.
    this.currentDistance =
      distance < this.currentDistance
        ? distance
        : this.currentDistance + (distance - this.currentDistance) * lerp;

    this.desired.copy(this.focus).addScaledVector(offset, this.currentDistance);
    this.camera.position.copy(this.desired);
    this.camera.lookAt(this.focus);
  }

  resize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }
}
